// API Service - Central HTTP client for all API calls
// Handles auth token, request/response interceptors, and type-safe methods

import { UserProfile, Appointment, DailyCheckIn, DailyGuidance } from '../types';

const API_BASE = '/api';

// Get auth token from localStorage (cookie is HttpOnly and sent automatically)
function getAuthToken(): string | null {
    return localStorage.getItem('coexpect_token');
}

export function setAuthToken(token: string): void {
    localStorage.setItem('coexpect_token', token);
}

export function clearAuthToken(): void {
    localStorage.removeItem('coexpect_token');

    // Clear all coexpect-related cookies
    const cookiesToClear = ['coexpect_session', 'coexpect_token', 'coexpect_auth'];
    const expireDate = 'Thu, 01 Jan 1970 00:00:01 GMT';

    cookiesToClear.forEach(name => {
        // Clear with various path combinations to ensure complete removal
        document.cookie = `${name}=; Path=/; Expires=${expireDate};`;
        document.cookie = `${name}=; Path=/; Expires=${expireDate}; Domain=${window.location.hostname};`;
        document.cookie = `${name}=; Expires=${expireDate};`;
    });
}

// Generic fetch wrapper with auth
async function apiFetch<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const token = getAuthToken();

    // SECURITY: Debug logging removed - token info should never be logged

    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
        // NOTE: Don't use credentials: 'include' - it conflicts with CORS origin: '*'
        // and causes browser to strip Authorization header. We use Bearer token, not cookies.
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));

        console.error('[API] Request failed:', endpoint, response.status, errorData);

        // NOTE: Removed auto-clear on 401 to prevent race condition clearing fresh tokens
        // The logout() function should be called explicitly when needed

        throw new Error((errorData as any).error || 'Request failed');
    }

    return response.json();
}

// ============ Auth API ============

export const authApi = {
    async login(email: string): Promise<{ success: boolean; message: string; devLink?: string }> {
        return apiFetch('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    },

    async verify(token: string): Promise<{
        success: boolean;
        token: string;
        isNewUser: boolean;
        user: UserProfile;
    }> {
        const result = await apiFetch<any>(`/auth/verify?token=${token}`, {
            method: 'GET',
        });

        if (result.token) {
            setAuthToken(result.token);
        }

        return result;
    },

    async verifyGoogle(credential: string): Promise<{
        success: boolean;
        token: string;
        isNewUser: boolean;
        user: UserProfile;
    }> {
        console.log('[Auth] verifyGoogle called');
        const result = await apiFetch<any>('/auth/google', {
            method: 'POST',
            body: JSON.stringify({ credential }),
        });

        console.log('[Auth] verifyGoogle result:', {
            success: result.success,
            hasToken: !!result.token,
            tokenLength: result.token?.length,
            isNewUser: result.isNewUser
        });

        if (result.token) {
            console.log('[Auth] Saving token to localStorage');
            setAuthToken(result.token);
            console.log('[Auth] Token saved, verifying:', !!localStorage.getItem('coexpect_token'));
        } else {
            console.warn('[Auth] No token in result!');
        }

        return result;
    },

    logout(): void {
        clearAuthToken();
    },

    isAuthenticated(): boolean {
        return !!getAuthToken();
    },
};

// ============ User API ============

export const userApi = {
    async getProfile(): Promise<UserProfile> {
        return apiFetch('/user');
    },

    async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
        return apiFetch('/user', {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    },

    // CCPA/GDPR: Export all user data
    async exportData(): Promise<Blob> {
        const response = await fetch('/api/user/data', {
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
            },
        });
        if (!response.ok) throw new Error('Export failed');
        return response.blob();
    },

    // CCPA/GDPR: Delete account and all data
    async deleteAccount(): Promise<{ success: boolean; message: string }> {
        return apiFetch('/user/data', {
            method: 'DELETE',
        });
    },

    async completeOnboarding(profile: {
        name: string;
        partnerName: string;
        role: 'partner' | 'pregnant';
        dueDate: string;
        focusAreas?: string[];
        comparisonTheme?: string;
        termsAcceptedAt?: string;
        ageVerified?: boolean;
    }): Promise<UserProfile> {
        return apiFetch('/user', {
            method: 'POST',
            body: JSON.stringify(profile),
        });
    },
};

// ============ Appointments API ============

export const appointmentsApi = {
    async list(): Promise<Appointment[]> {
        return apiFetch('/appointments');
    },

    async get(id: string): Promise<Appointment> {
        return apiFetch(`/appointments?id=${id}`);
    },

    async create(appointment: Omit<Appointment, 'id'>): Promise<Appointment> {
        return apiFetch('/appointments', {
            method: 'POST',
            body: JSON.stringify(appointment),
        });
    },

    async update(id: string, updates: Partial<Appointment>): Promise<Appointment> {
        return apiFetch(`/appointments?id=${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    },

    async delete(id: string): Promise<void> {
        await apiFetch(`/appointments?id=${id}`, {
            method: 'DELETE',
        });
    },
};

// ============ Check-ins API ============

export const checkinsApi = {
    async getByMonth(month: string): Promise<Record<string, DailyCheckIn>> {
        return apiFetch(`/checkins?month=${month}`);
    },

    async save(checkin: DailyCheckIn): Promise<DailyCheckIn> {
        return apiFetch('/checkins', {
            method: 'POST',
            body: JSON.stringify(checkin),
        });
    },
};

// ============ Guidance API ============

export const guidanceApi = {
    async getByDate(date: string): Promise<DailyGuidance | null> {
        return apiFetch(`/guidance?date=${date}`);
    },

    async save(date: string, guidance: DailyGuidance): Promise<DailyGuidance> {
        return apiFetch('/guidance', {
            method: 'POST',
            body: JSON.stringify({ date, ...guidance }),
        });
    },
};

// ============ Sync API ============

interface DataConflict {
    dataType: 'birth-plan' | 'hospital-bag' | 'name-shortlist' | 'saved-facts' | 'reminders' | 'checklists';
    yourData: any;
    yourUpdatedAt: number;
    partnerData: any;
    partnerUpdatedAt: number;
}

interface ConnectPartnerResult {
    success?: boolean;
    partnerName?: string;
    hasConflict?: boolean;
    conflictType?: 'due_date' | 'data';
    // Due date conflict fields
    yourDueDate?: string;
    partnerDueDate?: string;
    partnerRole?: 'partner' | 'pregnant';
    code?: string;
    // Data conflict fields
    dataConflicts?: DataConflict[];
}

interface ConflictResolution {
    dataType: 'birth-plan' | 'hospital-bag' | 'name-shortlist' | 'saved-facts' | 'reminders' | 'checklists';
    keepVersion: 'mine' | 'partner';
}

interface DataBackup {
    id: string;
    dataType: string;
    wasYours: boolean;
    resolvedAt: number;
}

export const syncApi = {
    async generateInviteCode(): Promise<{ code: string }> {
        return apiFetch('/sync/invite', {
            method: 'POST',
        });
    },

    async connectPartner(
        code: string,
        options?: {
            forceLink?: boolean;
            chosenDueDate?: string;
            syncDueDateToBoth?: boolean;
            dataConflictsResolved?: boolean;
        }
    ): Promise<ConnectPartnerResult> {
        return apiFetch('/sync/connect', {
            method: 'POST',
            body: JSON.stringify({ code, ...options }),
        });
    },

    async unlinkPartner(): Promise<{ success: boolean; message: string }> {
        return apiFetch('/sync/unlink', {
            method: 'DELETE',
        });
    },

    async resolveConflicts(
        partnerId: string,
        resolutions: ConflictResolution[]
    ): Promise<{ success: boolean }> {
        return apiFetch('/sync/resolve-conflicts', {
            method: 'POST',
            body: JSON.stringify({ partnerId, resolutions }),
        });
    },

    async getDataBackups(): Promise<DataBackup[]> {
        return apiFetch('/sync/revert-data');
    },

    async revertData(dataType: string): Promise<{ success: boolean }> {
        return apiFetch('/sync/revert-data', {
            method: 'POST',
            body: JSON.stringify({ dataType }),
        });
    },
};


// ============ User Data API (unified for efficiency) ============

export const userDataApi = {
    // Weight tracking
    async getWeightLog(): Promise<{ date: string; weight: number }[]> {
        return apiFetch('/user-data?type=weight');
    },
    async saveWeight(date: string, weight: number): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'weight', data: { date, weight } }),
        });
    },

    // Kick counter
    async getKickSessions(): Promise<{ id: string; startedAt: string; endedAt?: string; kickCount: number; kickTimestamps?: number[] }[]> {
        return apiFetch('/user-data?type=kicks');
    },
    async saveKickSession(session: { id?: string; startedAt: string; endedAt?: string; kickCount: number; kickTimestamps?: number[] }): Promise<{ id?: string }> {
        return apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'kick-session', data: session }),
        });
    },

    // Hospital bag
    async getHospitalBag(): Promise<any[]> {
        return apiFetch('/user-data?type=hospital-bag');
    },
    async saveHospitalBag(items: any[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'hospital-bag', data: items }),
        });
    },

    // Name shortlist
    async getNameShortlist(): Promise<any[]> {
        return apiFetch('/user-data?type=names');
    },
    async saveNameShortlist(names: any[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'names', data: names }),
        });
    },

    // Bump photos (thumbnails for cross-device sync)
    async getBumpPhotos(): Promise<{ id: string; week: number; date: string; thumbnail: string; caption: string }[]> {
        return apiFetch('/user-data?type=photos');
    },
    async saveBumpPhoto(photo: { week: number; date: string; thumbnail: string; caption: string }): Promise<{ id: string }> {
        return apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'photo', data: photo }),
        });
    },
    async deleteBumpPhoto(id: string): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'photo-delete', data: { id } }),
        });
    },

    // Saved facts (synced between partner accounts)
    async getSavedFacts(): Promise<string[]> {
        return apiFetch('/user-data?type=saved-facts');
    },
    async saveFacts(factIds: string[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'saved-facts', data: factIds }),
        });
    },

    // Flash facts progress (remembers where user left off)
    async getFlashFactsProgress(): Promise<{ index: number; filter: string } | null> {
        return apiFetch('/user-data?type=flash-facts-progress');
    },
    async saveFlashFactsProgress(progress: { index: number; filter: string }): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'flash-facts-progress', data: progress }),
        });
    },

    // Birth plan (synced across devices)
    async getBirthPlan(): Promise<any | null> {
        return apiFetch('/user-data?type=birth-plan');
    },
    async saveBirthPlan(planData: any): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'birth-plan', data: planData }),
        });
    },

    // Reminders (synced across devices)
    async getReminders(): Promise<any[]> {
        return apiFetch('/user-data?type=reminders');
    },
    async saveReminders(reminders: any[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'reminders', data: reminders }),
        });
    },

    // Symptoms
    async getSymptoms(): Promise<Record<string, any>> {
        return apiFetch('/user-data?type=symptoms');
    },

    async saveSymptoms(symptoms: Record<string, any>): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'symptoms', data: symptoms }),
        });
    },

    // Contractions (critical labor data - needs to persist!)
    async getContractions(): Promise<any[]> {
        return apiFetch('/user-data?type=contractions');
    },
    async saveContractions(contractions: any[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'contractions', data: contractions }),
        });
    },

    // Checklists (car seat, nursery, emergency prep)
    async getChecklists(): Promise<Record<string, string[]>> {
        return apiFetch('/user-data?type=checklists');
    },
    async saveChecklists(checklists: Record<string, string[]>): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'checklists', data: checklists }),
        });
    },

    // Providers (doctor contacts, insurance)
    async getProviders(): Promise<{ providers: any[]; insurancePhoto?: string }> {
        return apiFetch('/user-data?type=providers');
    },
    async saveProviders(data: { providers: any[]; insurancePhoto?: string }): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'providers', data }),
        });
    },

    // Blood Pressure Log
    async getBloodPressure(): Promise<any[]> {
        return apiFetch('/user-data?type=blood-pressure');
    },
    async saveBloodPressure(readings: any[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'blood-pressure', data: readings }),
        });
    },

    // Water Intake Tracker
    async getWaterIntake(): Promise<{ today: { date: string; amount: number } | null; history: any[]; goal: number }> {
        return apiFetch('/user-data?type=water-intake');
    },
    async saveWaterIntake(data: { today: { date: string; amount: number }; history: any[]; goal: number }): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'water-intake', data }),
        });
    },

    // Pelvic Floor Exercise Sessions
    async getPelvicFloorSessions(): Promise<{ id: string; date: string; reps: number; holdDuration: number; timestamp: number }[]> {
        return apiFetch('/user-data?type=pelvic-floor');
    },
    async savePelvicFloorSession(session: { reps: number; holdDuration: number; date: string; timestamp: number }): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'pelvic-floor', data: session }),
        });
    },

    // Fundal Height Measurements
    async getFundalHeight(): Promise<{ id: string; date: string; week: number; measurement: number; timestamp: number }[]> {
        return apiFetch('/user-data?type=fundal-height');
    },
    async saveFundalHeight(entry: { date: string; week: number; measurement: number; timestamp: number }): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'fundal-height', data: entry }),
        });
    },

    // Preeclampsia Risk Factors
    async getPreeclampsiaRisk(): Promise<any | null> {
        return apiFetch('/user-data?type=preeclampsia-risk');
    },
    async savePreeclampsiaRisk(factors: any): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'preeclampsia-risk', data: factors }),
        });
    },

    // EPDS Mental Health Screening History
    async getEPDSHistory(): Promise<{ id: string; score: number; answers: number[]; timestamp: number }[]> {
        return apiFetch('/user-data?type=epds-history');
    },
    async saveEPDSHistory(history: { id: string; score: number; answers: number[]; timestamp: number }[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'epds-history', data: history }),
        });
    },

    // Glucose Readings (GDM Tracking)
    async getGlucoseReadings(): Promise<{ id: string; value: number; timing: string; notes?: string; timestamp: number }[]> {
        return apiFetch('/user-data?type=glucose-readings');
    },
    async saveGlucoseReadings(readings: { id: string; value: number; timing: string; notes?: string; timestamp: number }[]): Promise<void> {
        await apiFetch('/user-data', {
            method: 'POST',
            body: JSON.stringify({ type: 'glucose-readings', data: readings }),
        });
    },
};

// ============ Chat API (Community) ============

export const chatApi = {
    // Get messages for a cohort week
    async getMessages(week: number): Promise<any[]> {
        return apiFetch(`/chat?week=${week}`);
    },

    // Post a new message
    async postMessage(data: { text: string; senderName: string; week: number; isAnonymous?: boolean; poll?: any }): Promise<{ id: string; timestamp: number }> {
        return apiFetch('/chat', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // Get daily thread for a week and role (separate threads for partners vs pregnant)
    async getThread(week: number, role: 'partner' | 'pregnant' = 'pregnant'): Promise<{ id: string; title: string; content: string; replies: any[] }> {
        return apiFetch(`/chat/threads?week=${week}&role=${role}`);
    },

    // Reply to a thread
    async postReply(data: { threadId: string; text: string; senderName: string; isAnonymous?: boolean }): Promise<{ id: string; timestamp: number }> {
        return apiFetch('/chat/threads', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // Soft delete a reply (users can only delete their own)
    async deleteReply(replyId: string): Promise<{ success: boolean }> {
        return apiFetch('/chat/threads', {
            method: 'DELETE',
            body: JSON.stringify({ replyId }),
        });
    },
};

// ============ Search API (fallback for when static content doesn't match) ============

export const searchApi = {
    query: async (query: string, role: string, week: number): Promise<{ answer: string; sources?: any[] }> => {
        try {
            const response = await apiFetch<{ success: boolean; answer?: string; sources?: any[]; error?: string }>('/search', {
                method: 'POST',
                body: JSON.stringify({ query, role, week })
            });

            if (response.success && response.answer) {
                return { answer: response.answer, sources: response.sources };
            }
            throw new Error(response.error || 'Search failed');
        } catch (error) {
            console.error('Search API error:', error);
            return { answer: "I couldn't find specific information for that. Try asking about common pregnancy topics like symptoms, nutrition, or preparing for baby." };
        }
    }
};

// ============ Partner Sync API (Shared Todos) ============

interface SharedTodo {
    id: string;
    text: string;
    isCompleted: boolean;
    createdBy: string;
    completedBy: string | null;
    dueDate: string | null;
    category: string;
    createdAt: number;
}

export const partnerSyncApi = {
    // Get shared todos
    async getTodos(): Promise<SharedTodo[]> {
        return apiFetch('/partner-sync?type=todos');
    },

    // Create new shared todo
    async createTodo(text: string, category?: string, dueDate?: string): Promise<{ id: string; success: boolean }> {
        return apiFetch('/partner-sync', {
            method: 'POST',
            body: JSON.stringify({ type: 'todo', text, category, dueDate }),
        });
    },

    // Toggle todo completion
    async updateTodo(id: string, isCompleted: boolean): Promise<{ success: boolean }> {
        return apiFetch('/partner-sync', {
            method: 'PUT',
            body: JSON.stringify({ id, isCompleted }),
        });
    },

    // Delete todo
    async deleteTodo(id: string): Promise<{ success: boolean }> {
        return apiFetch(`/partner-sync?id=${id}`, {
            method: 'DELETE',
        });
    },
};

// Default export with all APIs
export default {
    auth: authApi,
    user: userApi,
    appointments: appointmentsApi,
    checkins: checkinsApi,
    guidance: guidanceApi,
    sync: syncApi,
    userData: userDataApi,
    chat: chatApi,
    search: searchApi,
    partnerSync: partnerSyncApi,
};
